<script setup lang="ts">
import { computed, ref } from 'vue'
import CUIcon from '@/components/CUIcon.vue'
import { ReservationDetail } from '@/models/dto';
import { DateTime } from 'luxon'
import { currencyFilter } from '@/utils/string';

const props = defineProps<{
  reservation: ReservationDetail
}>()

const copiedField = ref('')
let timeoutId: ReturnType<typeof setTimeout> | null = null

const copy = async (text: string, label: string) => {
  await navigator.clipboard.writeText(text)
  copiedField.value = label
  if (timeoutId) clearTimeout(timeoutId)
  timeoutId = setTimeout(() => {
    copiedField.value = ''
  }, 2000)
}

const formattedAmount = computed(() => {
  return currencyFilter(props.reservation.balance);
});

const formattedDueDate = computed(() => {
  return DateTime.fromISO(props.reservation.dueDate)
    .toLocaleString({
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
})

</script>

<template>
  <div class="padding-x-2 mt-4">
    <template v-if="props.reservation.dueDate">
      <div class="font-14 margin-b-6">
        Please provide proof from your financial institution that can validate payment has been sent to CharterUP at the account listed by emailing
        <a href="mailto:billing@charterup.com" class="text-primary text-decoration-underline">billing@charterup.com</a>.
      </div>
    </template>
    <template v-else>
      <p class="font-14 margin-b-6">
        Please see below for how to send your payment by wire. For any questions, please email
        <a href="mailto:billing@charterup.com" class="text-primary text-decoration-underline">billing@charterup.com</a>.
      </p>
    </template>
    <div class="font-16 font-bold margin-b-4">Pay by Wire (Instructions)</div>
    <div class="margin-b-4">
      <div class="d-flex justify-space-between margin-b-2">
        <span class="font-14">Recipient Name:</span>
        <span class="font-14 font-weight-600 ">CharterUP LLC</span>
      </div>
      <div class="d-flex justify-space-between">
        <span class="font-14">Recipient Address:</span>
        <span class="font-14 font-weight-600 text-right" style="white-space: pre-line;">
          6595 Roswell Rd<br>Ste G291<br>Atlanta, GA 30328
        </span>
      </div>
    </div>
    <v-divider class="margin-y-4" />
    <div class="margin-b-4">
      <div class="d-flex justify-space-between margin-b-2">
        <span class="font-14">Bank Name:</span>
        <span class="font-14 font-weight-600">JP Morgan Chase</span>
      </div>
      <div class="d-flex justify-space-between">
        <span class="font-14">Bank Address:</span>
        <span class="font-14 font-weight-600 text-right" style="white-space: pre-line;">
          270 Park Avenue<br>New York, NY 10017
        </span>
      </div>
    </div>
    <v-divider class="margin-y-4" />
    <div class="margin-b-4">
      <div class="d-flex justify-space-between align-center margin-b-2">
        <span class="font-14">Account Number:</span>
        <span class="font-14 font-weight-600 d-flex flex-column align-end">
          <span v-if="copiedField === 'account'" class="text-primary font-12 font-weight-600 align-self-center d-flex align-start"><CUIcon color="primary" height="20" width="20">check</CUIcon> Copied!</span>
          <span class="d-flex align-center">
            <span>{{ '888756209' }}</span>
            <CUIcon view-box="0 0 24 24" height="15" width="15" color="primary" class="margin-l-1 cursor-pointer" @click="copy('888756209', 'account')">copy_content</CUIcon>
          </span>
        </span>
      </div>
      <div class="d-flex justify-space-between align-center">
        <span class="font-14">Routing Number:</span>
        <span class="font-14 font-weight-600 d-flex flex-column align-end">
          <span v-if="copiedField === 'routing'" class="text-primary font-12 font-weight-600 align-self-center d-flex align-start"><CUIcon color="primary" height="20" width="20">check</CUIcon> Copied!</span>
          <span class="d-flex align-center">
            <span>{{ '021000021' }}</span>
            <CUIcon view-box="0 0 24 24" height="15" width="15" color="primary" class="margin-l-1 cursor-pointer" @click="copy('021000021', 'routing')">copy_content</CUIcon>
          </span>
        </span>
      </div>
    </div>
    <div class="d-flex margin-t-6">
      <CUIcon view-box="0 0 24 24" color="primary" class="margin-r-2">
              info_outlined
            </CUIcon>
      <span class="font-italic font-14">
        Wire transfers are usually processed within 1 business day, but may vary depending on your bank.
      </span>
    </div>
  </div>
</template>
